import axios from 'axios';
import type { AxiosResponse } from 'axios';
import base64 from 'base-64';

import type {
  CheckHashRequest,
  CheckHashResponse,
  CreateAccountRequest,
  CreateAccountResponse,
  GetAccountEmailRequest,
  GetAccountEmailResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SetPasswordRequest,
  SetPasswordResponse,
} from 'api/models';

import { localStorage } from 'shared/utils';
import { LocalStorageKey } from 'shared/types';
import { getCookieExpirationDate, setCookie } from '../../../utils/cookies';
import type { CookieOptions } from '../../../utils/cookies';

interface CookieValue {
  name: string;
  value: string | number | boolean;
  options: CookieOptions;
}

interface LoginEvent {
  event: 'login';
  userId: string;
}

interface LoginWindow extends Window {
  dataLayer?: LoginEvent[];
}

interface SubmitResponseData {
  email?: string;
  data?: {
    id?: string;
    shopId?: number;
  };
}

type SetPasswordSubmitRequest = SetPasswordRequest & { remember: boolean };
type SubmitData =
  | CheckHashRequest
  | CreateAccountRequest
  | GetAccountEmailRequest
  | LoginRequest
  | RegisterRequest
  | ResetPasswordRequest
  | SetPasswordSubmitRequest;
type SubmitPath =
  | 'create-account'
  | 'create-account/submit'
  | 'login'
  | 'register'
  | 'user/password/reset'
  | 'user/password/set-new'
  | 'user/password/validate-link';

export const localStorageKeys = [LocalStorageKey.TOKEN, LocalStorageKey.SESSION_ID];

function updateLocalStore(values: Record<string, string>) {
  Object.entries(values).forEach(([key, value]) => {
    const item = localStorageKeys.find((el) => el.toLowerCase() === key);
    if (item) localStorage.setItem(item, value);
  });
}

function mapAndSetCookies(values: CookieValue[]) {
  values.forEach(({ name, value, options }) => setCookie(name, value, options));
}

function submit(data: LoginRequest, path: 'login'): Promise<AxiosResponse<LoginResponse>>;
function submit(
  data: GetAccountEmailRequest,
  path: 'create-account',
): Promise<AxiosResponse<GetAccountEmailResponse>>;
function submit(
  data: CreateAccountRequest,
  path: 'create-account/submit',
): Promise<AxiosResponse<CreateAccountResponse>>;
function submit(
  data: RegisterRequest,
  path: 'register',
): Promise<AxiosResponse<RegisterResponse>>;
function submit(
  data: ResetPasswordRequest,
  path: 'user/password/reset',
): Promise<AxiosResponse<ResetPasswordResponse>>;
function submit(
  data: CheckHashRequest,
  path: 'user/password/validate-link',
): Promise<AxiosResponse<CheckHashResponse>>;
function submit(
  data: SetPasswordSubmitRequest,
  path: 'user/password/set-new',
): Promise<AxiosResponse<SetPasswordResponse>>;
async function submit(
  data: SubmitData,
  path: SubmitPath,
): Promise<AxiosResponse<unknown>> {
  try {
    const response: AxiosResponse<SubmitResponseData> = await axios.post(
      `${process.env.REACT_APP_API_URL}${path}`,
      data,
    );

    const initialStorage: Record<string, string> = {
      token: response.headers.authorization,
    };

    const initialCookies: CookieValue[] = [
      {
        name: 'upcuid',
        value: response.data?.data?.shopId ?? '',
        options: {
          domain: '.uptain.de',
          expires: getCookieExpirationDate(
            new Date(),
            'remember' in data && data.remember ? { year: 1 } : { minutes: 240 },
          ),
        },
      },
    ];

    if (response.status === 200) {
      if (path === 'login') {
        const local = {
          ...initialStorage,
          get sessionid(): string {
            const decodeVal: string =
              localStorage.getItem(LocalStorageKey.TOKEN) || initialStorage.token;
            const payload: { session: string } = JSON.parse(
              base64.decode(decodeVal.split('.')[1]),
            );
            return payload.session;
          },
        };

        updateLocalStore(local);
        mapAndSetCookies(initialCookies);

        /**
         * Push the User ID to the Data Layer
         * needed for GA
         */
        const loginWindow: LoginWindow = window;
        loginWindow.dataLayer = loginWindow.dataLayer || [];
        loginWindow.dataLayer.push({
          event: 'login',
          userId: response.data?.data?.id ?? '',
        });
      }

      if (path === 'create-account/submit') {
        updateLocalStore(initialStorage);
        mapAndSetCookies(initialCookies);
      }

      if (path === 'user/password/set-new') {
        updateLocalStore(initialStorage);
        mapAndSetCookies(initialCookies);
      }
    }

    return response;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(String(error.response.status));
    }
    throw error;
  }
}

export { submit };
